import React from "react";
import { motion } from "framer-motion";
import { FaLinkedin, FaQuoteLeft } from "react-icons/fa";
import pkb from "../../assets/pkb.jpg";
import bk from "../../assets/bk.jpg";

const LeadershipSection = () => {
  const leadershipData = [
    {
      name: "Bharani Kumar Depuru",
      role: "Founder & CEO, AiSPRY | 360DigiTMG",
      linkedIn: "https://www.linkedin.com/in/bharanikumardepuru/",
      credentials:
        "Alumnus – ISB & IIT | 40 Under 40 Startup Leader | Chief Data Scientist",
      image: bk,
      testimonial: [
        "With over 20 years of experience in AI services, digital transformation, and technology consulting, Bharani Kumar Depuru is a visionary leader redefining how AI is applied across industries. As the Founder & CEO of AiSPRY, he has led 130+ consulting engagements and successfully delivered 80+ real-world AI and Data Science projects, solving high-impact challenges in manufacturing, healthcare, edtech, construction, and supply chain domains.",
        "Double alumnus of ISB and IIT, and certified in PMP, PMI-ACP, Lean Six Sigma, Scrum, and Tableau, Bharani brings rare expertise that bridges business strategy and advanced technology. His work has been recognized by CII, CAHOTECH, and Bharat Startup Awards, including the prestigious Best Practice in Digital Transformation award for AiTutor—an AI-powered learning system that transforms the education experience.",
        "He is a patent holder, author of 30+ international research papers, and a visiting faculty at Indian School of Business (ISB), where he mentors the next generation of data leaders. Under his leadership, AiSPRY is not only innovating AI-powered solutions for clients like Dr. Reddy’s, Siemens, and Biological E., but also enabling India’s workforce through 360DigiTMG’s practical, project-driven AI education.",
        "Driven by a belief that real-world projects are the true measure of learning, Bharani inspires thousands of students and professionals to move beyond theory and build solutions that matter.",
      ],
      alignment: "left",
    },
    {
      name: "Praveen Kumar Burra",
      role: "Co-Founder & CIO, AiSPRY",
      linkedIn: "https://www.linkedin.com/in/praveenkumarburra/",
      credentials:
        "20+ Years in Tech | AI Product Builder | Industry Solutions Expert",
      image: pkb,
      testimonial: [
        "I’ve spent the last 20+ years navigating the tech world — building products, managing services, engaging with global clients, and solving problems across industries like Oil & Gas, Pharma, Manufacturing, and Automotive. Along the way, one thing became clear: technology only creates value when it truly understands the business it's serving.",
        "That insight sparked my transition into AI. Not just as a buzzword — but as a tool to simplify decisions, automate intelligently, and bring clarity where there’s complexity.",
        "At AiSPRY, I lead our mission to build practical AI solutions that actually move the needle. From forecasting trends, optimizing processes, or automating workflows — we don’t just deliver models; we deliver measurable outcomes.",
        "What drives me is creating solutions that work in the real world. I love working closely with clients, rolling up my sleeves, and designing systems that are not only smart — but explainable, adaptable, and built for long-term impact.",
        "Thanks for stopping by. I’d love to connect and explore how AI can power your next big leap.",
      ],
      alignment: "right",
    },
  ];

  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.8 } },
  };

  const slideIn = {
    hidden: { opacity: 0, x: -100 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
  };

  const slideInRight = {
    hidden: { opacity: 0, x: 100 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
  };

  return (
    <section className="pb-10 pt-10 bg-gradient-to-b from-blue-50 to-indigo-50">
      <div className="container mx-auto px-5 max-w-8xl">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-[#108dc7] to-[#ef8e38] bg-clip-text text-transparent">
            Leadership Vision
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            The minds shaping AiSPRY's innovative approach to AI solutions
          </p>
        </motion.div>

        {leadershipData.map((leader, index) => (
          <div
            key={index}
            className={`flex flex-col ${
              leader.alignment === "left"
                ? "lg:flex-row"
                : "lg:flex-row-reverse"
            } items-center justify-center gap-10 mb-20 mx-auto`}
          >
            {/* Image */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={leader.alignment === "left" ? slideIn : slideInRight}
              className=" relative group"
            >
              <div className="relative overflow-hidden rounded-2xl shadow-2xl ">
                <img
                  src={leader.image}
                  alt={leader.name}
                  className="w-auto h-[35rem] object-fit transform group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-end p-6">
                  <div>
                    <h3 className="text-2xl font-bold text-white">
                      {leader.name}
                    </h3>
                    <p className="text-blue-200 font-large">{leader.role}</p>
                    <p className="text-blue-100 text-sm mt-1">
                      {leader.credentials}
                    </p>
                    <a
                      href={leader.linkedIn}
                      className="inline-flex items-center mt-3 text-white hover:text-blue-300 transition-colors"
                    >
                      <FaLinkedin className="mr-2" />
                      Connect on LinkedIn
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Testimonial */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={leader.alignment === "left" ? slideInRight : slideIn}
              className="lg:w-1/2"
            >
              <div className="bg-white p-8 rounded-2xl shadow-lg  border-l-4 border-[#108dc7]">
                <FaQuoteLeft className="text-4xl text-gray-200 absolute top-6 left-6 md:inline-block" />
                <div className="pl-0 md:pl-10">
                  {leader.testimonial.map((para, i) => (
                    <p key={i} className="text-gray-700 mb-4">
                      {para}
                    </p>
                  ))}
                </div>
                <div className="flex items-center  justify-end mt-6">
                  <div>
                    <h4 className="font-bold text-gray-800">{leader.name}</h4>
                    <p className="text-sm text-gray-600">{leader.role}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default LeadershipSection;
